/**
 * ItemCard — generic equipment item card.
 *
 * variant="officer" (Recorders):
 *   Click Not Avail → prompt for name → blue + last name.
 *   Click blue name row → return to rack.
 *
 * variant="spare" (Badges):
 *   Click Available → inline text input → type name + Enter → blue + last name.
 *   Click blue name → return to rack.
 */
import { useState, useRef, useEffect } from 'react'
import { Card, StatusDot, dotLabelColor } from '../ui'
import { markDirty } from '../../stores/sync'
import type { GenericItem } from '../../stores/desktop'

interface ItemCardProps {
  item: GenericItem
  image: string
  itemLabel: string
  onUpdate: (patch: Partial<GenericItem>) => void
  /** 'officer' = prompt on Not Avail (like SecurityKeyCard/OfficerRadioCard)
   *  'spare'   = inline input on Available (like SpareRadioCard/OtherKeyCard)
   *  default   = original 3-row click-to-edit behaviour */
  variant?: 'officer' | 'spare'
}

function lastName(name: string): string {
  const s = name.replace(/\s*\(.*?\)\s*/g, '').trim()
  return s.includes(',') ? s.split(',')[0].trim() : (s.split(' ').pop() ?? s)
}

export default function ItemCard({ item, image, itemLabel, onUpdate, variant }: ItemCardProps) {
  const [editing, setEditing]   = useState(false)
  const [inputVal, setInputVal] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const isOos      = !!item.oos
  const isAssigned = !isOos && !!item.assignedTo && !item.available === false ? !!item.assignedTo : !isOos && !!item.assignedTo
  const isNotAvail = !isOos && !item.available && !item.assignedTo
  const isAvail    = !isOos && item.available && !item.assignedTo

  // Simpler state derivation
  const assigned   = !isOos && !!item.assignedTo
  const notAvail   = !isOos && !item.available && !item.assignedTo
  const avail      = !isOos && item.available && !item.assignedTo

  const borderColor = isOos    ? '#fca5a5'
                    : assigned ? '#3b82f6'
                    : notAvail ? '#f87171'
                    : editing  ? '#60a5fa'
                               : '#4ade80'

  const imgActive = assigned || avail || editing

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  // ── Officer variant handlers ──────────────────────────────────────────────
  function handleOfficerAvailable() {
    if (isOos) return
    markDirty()
    onUpdate({ available: true, assignedTo: '' })
  }

  function handleOfficerNotAvail() {
    if (isOos) return
    markDirty()
    if (avail || assigned) {
      const name = window.prompt(`${itemLabel} ${item.id} — Officer's name?`) || ''
      if (name.trim()) {
        onUpdate({ available: false, assignedTo: name.trim() })
      } else {
        onUpdate({ available: false, assignedTo: '' })
      }
    } else {
      onUpdate({ available: true, assignedTo: '' })
    }
  }

  // ── Spare variant handlers ────────────────────────────────────────────────
  function handleSpareAvailableClick() {
    if (isOos) return
    if (assigned) {
      markDirty()
      onUpdate({ available: true, assignedTo: '' })
    } else if (avail && !editing) {
      setInputVal('')
      setEditing(true)
    } else if (notAvail) {
      markDirty()
      onUpdate({ available: true, assignedTo: '' })
    }
  }

  function commitAssign() {
    const name = inputVal.trim()
    setEditing(false)
    setInputVal('')
    if (name) {
      markDirty()
      onUpdate({ available: false, assignedTo: name })
    }
  }

  function handleSpareKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') commitAssign()
    if (e.key === 'Escape') { setEditing(false); setInputVal('') }
  }

  function handleSpareNotAvail() {
    if (isOos) return
    setEditing(false)
    setInputVal('')
    markDirty()
    if (notAvail) {
      onUpdate({ available: true, assignedTo: '' })
    } else {
      onUpdate({ available: false, assignedTo: '' })
    }
  }

  // ── Officer variant render ────────────────────────────────────────────────
  if (variant === 'officer') {
    return (
      <Card variant="item" borderColor={borderColor} opacity={isOos ? 0.5 : 1}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6, textAlign: 'center' }}>
          {itemLabel} {item.id}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ flexShrink: 0, width: 60, height: 60 }}>
            <img src={image} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', opacity: imgActive ? 1 : 0.4, filter: imgActive ? 'none' : 'grayscale(100%)' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
            {isOos ? (
              <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }}>OOS</span>
            ) : (
              <>
                <div
                  onClick={handleOfficerAvailable}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: assigned || notAvail ? 'pointer' : 'default', opacity: avail || assigned ? 1 : 0.3 }}
                >
                  <StatusDot variant={assigned ? 'blue' : 'green'} active={avail || assigned} />
                  {assigned ? (
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {lastName(item.assignedTo)}
                    </span>
                  ) : (
                    <span style={{ fontSize: 11, fontWeight: 700, color: dotLabelColor('green', avail) }}>Available</span>
                  )}
                </div>
                <div
                  onClick={handleOfficerNotAvail}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', opacity: notAvail ? 1 : 0.3 }}
                >
                  <StatusDot variant="red" active={notAvail} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: dotLabelColor('red', notAvail) }}>Not Avail.</span>
                </div>
              </>
            )}
          </div>
        </div>
      </Card>
    )
  }

  // ── Spare variant render ──────────────────────────────────────────────────
  if (variant === 'spare') {
    return (
      <Card variant="item" borderColor={borderColor} opacity={isOos ? 0.5 : 1}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6, textAlign: 'center' }}>
          {itemLabel} {item.id}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ flexShrink: 0, width: 60, height: 60 }}>
            <img src={image} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', opacity: imgActive ? 1 : 0.4, filter: imgActive ? 'none' : 'grayscale(100%)' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
            {isOos ? (
              <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }}>OOS</span>
            ) : (
              <>
                {editing ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%' }}>
                    <input
                      ref={inputRef}
                      value={inputVal}
                      onChange={e => setInputVal(e.target.value)}
                      onKeyDown={handleSpareKeyDown}
                      onBlur={commitAssign}
                      placeholder="Name"
                      style={{ flex: 1, minWidth: 0, background: 'var(--bg-deep)', border: '1px solid #60a5fa', borderRadius: 4, color: 'var(--text-primary)', fontSize: 11, padding: '2px 6px', outline: 'none' }}
                    />
                    <span onMouseDown={e => { e.preventDefault(); commitAssign() }} style={{ fontSize: 13, cursor: 'pointer', color: '#60a5fa', fontWeight: 700 }}>✓</span>
                  </div>
                ) : (
                  <div
                    onClick={handleSpareAvailableClick}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', opacity: avail || assigned ? 1 : 0.3 }}
                  >
                    <StatusDot variant={assigned ? 'blue' : 'green'} active={avail || assigned} />
                    {assigned ? (
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {lastName(item.assignedTo)}
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, fontWeight: 700, color: dotLabelColor('green', avail) }}>Available</span>
                    )}
                  </div>
                )}
                <div
                  onClick={handleSpareNotAvail}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', opacity: notAvail ? 1 : 0.3 }}
                >
                  <StatusDot variant="red" active={notAvail} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: dotLabelColor('red', notAvail) }}>Not Avail.</span>
                </div>
              </>
            )}
          </div>
        </div>
      </Card>
    )
  }

  // ── Default variant (original 3-row behaviour) ────────────────────────────
  const [editingDef, setEditingDef] = useState(false)
  const [editValDef, setEditValDef] = useState('')

  function commitEditDef() {
    onUpdate({ assignedTo: editValDef.trim() })
    setEditingDef(false)
  }

  function handleNotAvailDef() {
    let name = item.assignedTo.trim()
    if (!name) name = window.prompt(`${itemLabel} ${item.id} — Who had it last?`) ?? ''
    if (!name) name = 'Unknown'
    onUpdate({ available: false, assignedTo: name })
  }

  return (
    <Card variant="item" borderColor={borderColor} opacity={isOos ? 0.5 : 1}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6, textAlign: 'center' }}>
        {itemLabel} {item.id}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ flexShrink: 0, width: 72, height: 72 }}>
          <img src={image} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', opacity: (assigned || avail) && !isOos ? 1 : 0.4, filter: (assigned || avail) && !isOos ? 'none' : 'grayscale(100%)' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
          {isOos ? (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }}>OOS</span>
          ) : (
            <>
              <div onClick={() => { onUpdate({ available: true, assignedTo: '' }); setEditingDef(false) }} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', opacity: avail ? 1 : 0.3 }}>
                <StatusDot variant="green" active={avail} size={12} />
                <span style={{ fontSize: 11, fontWeight: 700, color: dotLabelColor('green', avail) }}>Avail.</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, opacity: assigned || editingDef ? 1 : 0.5, width: '100%' }}>
                <StatusDot variant="blue" active={assigned} size={12} />
                {editingDef ? (
                  <input autoFocus type="text" value={editValDef} onChange={e => setEditValDef(e.target.value)} onBlur={commitEditDef} onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }} placeholder="Name..." style={{ flex: 1, fontSize: 11, border: '1px solid #fb923c', borderRadius: 4, padding: '1px 4px', background: 'var(--bg-deep)', color: 'var(--text-primary)', outline: 'none', minWidth: 0 }} />
                ) : assigned ? (
                  <span onClick={() => { setEditingDef(true); setEditValDef(item.assignedTo) }} title="Click to edit" style={{ fontSize: 11, fontWeight: 600, color: '#9a3412', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 80 }}>{lastName(item.assignedTo)}</span>
                ) : (
                  <span onClick={() => { setEditingDef(true); setEditValDef('') }} title="Click to assign" style={{ fontSize: 11, color: 'var(--text-hint)', cursor: 'pointer' }}>None</span>
                )}
              </div>
              <div onClick={handleNotAvailDef} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', opacity: notAvail ? 1 : 0.3 }}>
                <StatusDot variant="red" active={notAvail} size={12} />
                <span style={{ fontSize: 11, fontWeight: 700, color: dotLabelColor('red', notAvail) }}>Not Avail.</span>
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  )
}
